// The photo queue (D-92): the bytes of every declared photo, one PUT per
// photo, up to three in flight, and only once the server applied the
// revision that declares the photo. Its own cycle, apart from the data
// outbox: fired on start, on regaining connectivity, on every capture,
// after every data cycle and every sixty seconds while online.
//
// * a network error, a 5xx (the object store unavailable included), a 429 or
//   a 404 (the declaring revision not applied yet): the photo waits with the
//   data queue's backoff and is sent again whole. A photo resumes by itself,
//   never by fragments;
// * a 401 or a 403: back to pending, untouched; the session layer decides;
// * a photo over 8 MB is refused before any request; a checksum that does not
//   match, an oversized body, an unreadable image, a photo uploaded with other
//   bytes or deleted on the server are terminal and stay visible with their
//   code;
// * a success discards the local bytes, keeps the server's identifier for the
//   thumbnail, and carries the visit's completion onto the draft.
//
// INC-06b-2b (D-144, D-187): only the signed-in operator's own photos are sent.
// The server refuses the bytes of a photo declared by anyone else exactly as it
// refuses a photo that does not exist, so a photo of another operator would end
// terminal for no fault of its own; it is left untouched instead, and uploads
// when its author signs in.
import { isApiError } from '../api/errors';
import type { ApiClient } from '../api/client';
import type { PhotoUploadResult } from '../api/types';
import type { FieldcoreDb, PhotoQueueRecord } from '../db/database';
import { MAX_PHOTO_BYTES } from '../photos/image';
import { backoffDelayMs } from './backoff';

export const PHOTO_CONCURRENCY = 3;
export const PHOTO_PERIODIC_INTERVAL_MS = 60_000;
export const CHECKSUM_HEADER = 'X-Checksum-SHA256';

export function photoUploadPath(clientGeneratedId: string): string {
  return `/api/v1/sync/photos/${clientGeneratedId}`;
}

/** Answers that no retry can change. */
const TERMINAL_CODES = new Set([
  'checksum_mismatch',
  'photo_too_large',
  'photo_unreadable',
  'photo_already_uploaded',
  'photo_deleted',
]);

export interface PhotoRunResult {
  attempted: number;
  uploaded: number;
  unchanged: number;
  failed: number;
  rejected: number;
}

export interface PhotoQueueDeps {
  db: FieldcoreDb;
  api: ApiClient;
  isOnline?: () => boolean;
  /** The signed-in operator, whose photos are the only ones a run sends (D-187). */
  currentAuthor?: () => string | null;
  now?: () => number;
  random?: () => number;
  concurrency?: number;
}

type Outcome = 'uploaded' | 'unchanged' | 'failed' | 'rejected';

const EMPTY: PhotoRunResult = { attempted: 0, uploaded: 0, unchanged: 0, failed: 0, rejected: 0 };

function defaultOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  const online: unknown = Reflect.get(navigator, 'onLine');
  return online !== false;
}

export class PhotoQueue {
  private readonly db: FieldcoreDb;
  private readonly api: ApiClient;
  private readonly isOnline: () => boolean;
  private readonly currentAuthor: () => string | null;
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly concurrency: number;
  private inflight: Promise<PhotoRunResult> | null = null;
  private rerun = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private periodicTimer: ReturnType<typeof setInterval> | null = null;
  private readonly onOnline = (): void => {
    this.trigger();
  };

  constructor(deps: PhotoQueueDeps) {
    this.db = deps.db;
    this.api = deps.api;
    this.isOnline = deps.isOnline ?? defaultOnline;
    this.currentAuthor = deps.currentAuthor ?? ((): string | null => null);
    this.now = deps.now ?? (() => Date.now());
    this.random = deps.random ?? Math.random;
    this.concurrency = Math.max(1, deps.concurrency ?? PHOTO_CONCURRENCY);
  }

  start(): void {
    if (typeof window !== 'undefined') window.addEventListener('online', this.onOnline);
    this.periodicTimer ??= setInterval(() => {
      if (this.isOnline()) this.trigger();
    }, PHOTO_PERIODIC_INTERVAL_MS);
    void this.recoverStale().then(() => {
      this.trigger();
    });
  }

  stop(): void {
    if (typeof window !== 'undefined') window.removeEventListener('online', this.onOnline);
    if (this.periodicTimer !== null) clearInterval(this.periodicTimer);
    this.periodicTimer = null;
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  /** A photo left `uploading` by a page that went away is sent again whole.
   *  Only this session's own: another operator's is that operator's to recover. */
  async recoverStale(): Promise<void> {
    const at = this.now();
    const author = this.currentAuthor();
    await this.db.transaction('rw', this.db.photo_queue, async () => {
      const stale = (
        await this.db.photo_queue.where('status').equals('uploading').toArray()
      ).filter((record) => author === null || record.author_id === author);
      for (const record of stale) {
        if (record.deleted === 1) await this.db.photo_queue.delete(record.client_generated_id);
        else await this.db.photo_queue.put({ ...record, status: 'pending', updated_at: at });
      }
    });
  }

  trigger(): void {
    void this.runNow();
  }

  /** Run until nothing eligible is left, or join the run in flight. Never rejects. */
  runNow(): Promise<PhotoRunResult> {
    if (this.inflight !== null) {
      this.rerun = true;
      return this.inflight;
    }
    this.inflight = this.runUntilSettled().finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  private async runUntilSettled(): Promise<PhotoRunResult> {
    const total: PhotoRunResult = { ...EMPTY };
    for (;;) {
      const batch = await this.eligible();
      if (batch.length === 0) {
        if (this.rerun) {
          this.rerun = false;
          continue;
        }
        break;
      }
      const outcomes = await Promise.all(batch.map((record) => this.uploadOne(record)));
      total.attempted += outcomes.length;
      for (const outcome of outcomes) total[outcome] += 1;
      // A failure waits for its backoff; the batch that failed ends the run.
      if (outcomes.every((outcome) => outcome === 'failed')) break;
    }
    await this.scheduleRetry();
    return total;
  }

  /** Pending photos whose visit the server knows at the revision that declares them. */
  private async eligible(): Promise<PhotoQueueRecord[]> {
    if (!this.isOnline()) return [];
    // Nobody to send as: nothing of the queue is eligible.
    const author = this.currentAuthor();
    if (author === null) return [];
    const at = this.now();
    const pending = await this.db.photo_queue
      .where('status')
      .equals('pending')
      .sortBy('created_at');
    const batch: PhotoQueueRecord[] = [];
    for (const record of pending) {
      if (record.deleted === 1) continue;
      // D-187: another operator's photo is not this session's to upload.
      if (record.author_id !== author) continue;
      if (record.next_attempt_at !== null && record.next_attempt_at > at) continue;
      const draft = await this.db.visit_drafts.get(record.visit_client_generated_id);
      if (draft?.server_visit_id == null) continue;
      if (draft.synced_revision < record.declared_revision) continue;
      batch.push(record);
      if (batch.length >= this.concurrency) break;
    }
    return batch;
  }

  private async uploadOne(record: PhotoQueueRecord): Promise<Outcome> {
    const id = record.client_generated_id;
    if (
      record.data === null ||
      record.data.byteLength > MAX_PHOTO_BYTES ||
      record.bytes > MAX_PHOTO_BYTES
    ) {
      // Refused before sending: no request can carry it.
      await this.reject(record, 'photo_too_large', null);
      return 'rejected';
    }
    // The whole record, never a partial update: Dexie's update clones the
    // stored object and does not keep an ArrayBuffer, which would lose the photo.
    await this.db.photo_queue.put({ ...record, status: 'uploading', updated_at: this.now() });
    try {
      const result = await this.api.putBinary<PhotoUploadResult>(photoUploadPath(id), record.data, {
        'Content-Type': 'image/jpeg',
        [CHECKSUM_HEADER]: record.checksum,
      });
      await this.settle(record, result);
      return result.result === 'created' ? 'uploaded' : 'unchanged';
    } catch (error) {
      return this.fail(record, error);
    }
  }

  private async settle(record: PhotoQueueRecord, result: PhotoUploadResult): Promise<void> {
    const at = this.now();
    await this.db.transaction('rw', [this.db.photo_queue, this.db.visit_drafts], async () => {
      const current = await this.db.photo_queue.get(record.client_generated_id);
      if (current?.deleted === 1) {
        await this.db.photo_queue.delete(record.client_generated_id);
      } else if (current !== undefined) {
        await this.db.photo_queue.put({
          ...current,
          status: 'uploaded',
          data: null,
          server_photo_id: result.photo.id,
          attempts: current.attempts + 1,
          next_attempt_at: null,
          last_code: null,
          last_http_status: result.result === 'created' ? 201 : 200,
          updated_at: at,
        });
      }
      const draft = await this.db.visit_drafts.get(record.visit_client_generated_id);
      // Uploads settle in any order: an `unchanged` answer may carry the
      // visit as it was before a later upload completed it, so a draft never
      // goes back from complete.
      if (
        draft !== undefined &&
        draft.sync_status !== 'complete' &&
        (result.visit_sync_status === 'complete' ||
          result.visit_sync_status === 'data_synced_photos_pending')
      ) {
        await this.db.visit_drafts.put({
          ...draft,
          sync_status: result.visit_sync_status,
          updated_at: at,
        });
      }
    });
  }

  private async fail(record: PhotoQueueRecord, error: unknown): Promise<Outcome> {
    const id = record.client_generated_id;
    const current = await this.db.photo_queue.get(id);
    if (current === undefined) return 'failed';
    if (current.deleted === 1) {
      await this.db.photo_queue.delete(id);
      return 'failed';
    }
    const at = this.now();
    let status: number | null = null;
    let code: string | null = null;
    let delay: number | null = null;
    if (isApiError(error)) {
      status = error.status;
      code = error.code;
      if (error.status === 413 || TERMINAL_CODES.has(error.code)) {
        await this.reject(current, error.status === 413 ? 'photo_too_large' : error.code, status);
        return 'rejected';
      }
      if (error.status === 401 || error.status === 403) {
        await this.db.photo_queue.put({
          ...current,
          status: 'pending',
          last_code: code,
          last_http_status: status,
          updated_at: at,
        });
        return 'failed';
      }
      const retryable = error.status >= 500 || error.status === 429 || error.status === 404;
      if (!retryable) {
        await this.reject(current, code, status);
        return 'rejected';
      }
      if (error.status === 429) delay = error.retryAfterMs;
    }
    const attempts = current.attempts + 1;
    delay ??= backoffDelayMs(attempts, this.random);
    await this.db.photo_queue.put({
      ...current,
      status: 'pending',
      attempts,
      next_attempt_at: at + delay,
      last_code: code ?? 'network_error',
      last_http_status: status,
      updated_at: at,
    });
    return 'failed';
  }

  private async reject(
    record: PhotoQueueRecord,
    code: string,
    status: number | null,
  ): Promise<void> {
    await this.db.photo_queue.put({
      ...record,
      status: 'rejected',
      attempts: record.attempts + 1,
      next_attempt_at: null,
      last_code: code,
      last_http_status: status,
      updated_at: this.now(),
    });
  }

  /** One timer for the earliest photo waiting on its backoff. */
  private async scheduleRetry(): Promise<void> {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    const waiting = await this.db.photo_queue.where('status').equals('pending').toArray();
    const next = waiting
      .map((record) => record.next_attempt_at)
      .filter((value): value is number => value !== null)
      .sort((a, b) => a - b)[0];
    if (next === undefined) return;
    this.retryTimer = setTimeout(
      () => {
        this.retryTimer = null;
        this.trigger();
      },
      Math.max(0, next - this.now()),
    );
  }
}
